import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import Select from 'react-select'
import { reactSelectClassNames } from '../../../helpers/react-select-styles'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import SpecialInstruction from '../../../components/elements/SpecialInstruction'
import Loading from '../../../components/Loading'

export default function DeleteBureauTradelineModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { handleSubmit, control, reset } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const active = modal.is_active && modal.type === 'delete_bureau_tradelines'
  const customerId = modal.options?.customerId
  const [loans, setLoans] = useState([])
  const [gettingLoans, setGettingLoans] = useState(false)
  const inFlightCustomerId = useRef(null)

  const resetState = () => {
    reset({ loan_id: null })
    setLoans([])
    setGettingLoans(false)
    setSaving(false)
  }

  // This modal stays mounted in the Footer between openings, so the previous customer's loan list
  // and selected loan_id survive a close. Clear both on every open before loading this customer's
  // loans — otherwise submitting deletes tradelines for a loan the agent is no longer looking at.
  useEffect(() => {
    if (!active || !customerId) {
      inFlightCustomerId.current = null
      return
    }
    resetState()
    getLoans(customerId)
  }, [active, customerId])

  const getLoans = async (forCustomerId) => {
    inFlightCustomerId.current = forCustomerId
    setGettingLoans(true)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${forCustomerId}/loans`)
      const response = await request.get()
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightCustomerId.current !== forCustomerId) return

      setLoans((response?.data?.data ?? []).map((e) => {
        let creditReport = e.factor_trust_score ? '- FactorTrust' : '';
        creditReport += e.clarity_score ? '- Clarity' : '';
        return { label: `${e.guid} - ${e.effective_date} - $${e.principal_amount} ${creditReport}`, value: e.id }
      }))
    } catch (error) {
      if (inFlightCustomerId.current !== forCustomerId) return
      dispatch(setNotification({ type: 'error', body: error.response?.data?.message }))
    } finally {
      if (inFlightCustomerId.current === forCustomerId) setGettingLoans(false)
    }
  }

  const handleCancel = () => {
    inFlightCustomerId.current = null
    resetState()
    dispatch(setModal({ is_active: false }));
  };

  const onSubmit = async (formData) => {
    setSaving(true)
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${formData.loan_id.value}/delete_bureau_tradelines`)
    try {
      const response = await request.delete()
      if (response.status === 200) {
        handleCancel()
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
    setSaving(false)
  };
  return modal.is_active && modal.type === 'delete_bureau_tradelines' ? (
    <form onSubmit={handleSubmit(onSubmit)} id="delete_bureau_tradelines">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Delete Bureau Tradeline Entries
            </p>
            <button type="button" className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "110px", overflow: 'hidden' }}
          >
            <div className="columns mb-0 is-flex-direction-column">
              {gettingLoans && (
                <div className="columns mb-0 is-flex-direction-column">
                  <div className="column">
                    <span className="has-text-center"><Loading /></span>
                  </div>
                </div>
              )}
              <div className="column has-text-danger">
                <strong>Warning:</strong> This will remove all traces that this loan ever existed from the tradelines
              </div>
              <div className="column">
                <h3>Select Loan:</h3>
                <div className="mt-1">
                  <Controller
                    name="loan_id"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={loans}
                        isSearchable={false}
                        classNames={reactSelectClassNames()}
                        onChange={(val) => {
                          field.onChange({ label: val?.label, value: val?.value })
                        }}
                        menuPosition="fixed"
                        styles={{ menu: ({ ...css }) => ({ ...css, width: 'max-content' }) }}
                        form="delete_bureau_tradelines"
                      />
                    )}
                  />
                </div>
              </div>

              <div className="column">
                <SpecialInstruction
                  content={'<span>' +
                    "<span class='has-text-weight-bold'>This will perform the following actions:</span>" + '<br>' +
                    '<span class=\'pl-2\'>• Removal of all tradelines to this loan from all bureaus</span> <br> ' +
                    '<span class=\'pl-2\'>• Set loan is_bypass_bureau_reporting = TRUE</span> <br> ' +
                    ' </span>'}
                  type="is-warning"
                />
              </div>
            </div>
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={handleCancel}
              >Cancel
              </button>
              <button
                type="submit"
                className="button is-link is-small is-danger"
                disabled={saving && 'disabled'}
              >{saving ? 'Submitting' : 'Delete Tradelines'}</button>
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
